import { Box, Button, Card, CardContent, LinearProgress, Link, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import AuthLayout from "layouts/AuthLayout/AuthLayout";
import Logo from "assets/images/side_nav_inkfree_logo.svg";
import { useAppSelector } from "hooks/use-app-select";
import { useAppDispatch } from "hooks/use-app-dispatch";
import {
  logOutAction,
  selectAccessToken,
  selectAccount,
  selectAccountDisplayName,
  selectAuthenticated,
} from "redux/authSlice";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import urls from "global/constants/UrlConstants";
import strings from "global/constants/StringConstants";
import notifiers from "global/constants/NotificationConstants";
import title from "global/constants/TitleConstants";
import { useTitle } from "utils/UseTitle";
import { getErrorMessageText } from "helpers/methods";
import { hasAccessToAccount, isAdmin } from "layouts/Config";
import { refreshFeatures } from "redux/featuresSlice";
import { fetchCurrentPlan } from "Services/CommonServices";
import {
  AwsMarketplaceClaimInfo,
  getAwsMarketplaceClaim,
  linkAwsMarketplaceClaim,
} from "./AwsMarketplaceService";
import {
  awsMarketplaceErrorMessage,
  clearAwsMarketplaceClaim,
  isAccessTokenExpired,
  loadAwsMarketplaceClaim,
  parseAwsMarketplaceFragment,
  saveAwsMarketplaceClaim,
} from "./AwsMarketplaceHelper";
import styles from "./AwsMarketplaceSetup.styles";

type Stage =
  | { kind: "checking" }
  | { kind: "error"; message: string }
  | { kind: "alreadyLinked" }
  | { kind: "expired" }
  | { kind: "signIn"; info: AwsMarketplaceClaimInfo }
  | { kind: "confirm"; info: AwsMarketplaceClaimInfo }
  | { kind: "notAdmin" }
  | { kind: "linking"; info: AwsMarketplaceClaimInfo }
  | { kind: "linked"; planPending: boolean; planInactive?: boolean }
  | { kind: "linkFailed"; info: AwsMarketplaceClaimInfo; message: string };

/**
 * Where ss-core-service sends an AWS Marketplace buyer after resolving their registration
 * token (AwsMarketplaceRegistrationService). Like McpAuthorize it does not render a login form:
 * a signed-out buyer goes to the real Login screen with `?redirectionUrl=` so MFA, SSO and
 * Google sign-in all work, then comes back here. The one-time claim from the URL fragment is
 * kept in localStorage across that detour (and across sign-up's email verification).
 * Linking is an explicit button, not automatic, so a buyer signed into the wrong account
 * can switch first. Core enforces the admin rule too; the UI only mirrors it.
 */
const AwsMarketplaceSetup = () => {
  useTitle(title.AWS_MARKETPLACE_SETUP);
  const isLoggedIn = useAppSelector(selectAuthenticated);
  const accessToken = useAppSelector(selectAccessToken);
  const accountName = useAppSelector(selectAccountDisplayName);
  const account = useAppSelector(selectAccount);
  const dispatch = useAppDispatch();
  const [stage, setStage] = useState<Stage>({ kind: "checking" });
  const [claim, setClaim] = useState<string | undefined>();

  const signedIn = isLoggedIn && !isAccessTokenExpired(accessToken);

  useEffect(() => {
    const fragment = parseAwsMarketplaceFragment(window.location.hash);
    if (window.location.hash) {
      // Keep the claim out of the address bar, history and any later Referer.
      history.replace(viewpaths.awsMarketplaceSetupViewPath);
    }
    if (fragment.claim) {
      saveAwsMarketplaceClaim(fragment.claim);
    }
    if (fragment.error) {
      setStage({ kind: "error", message: awsMarketplaceErrorMessage(fragment.error) });
      return;
    }
    if (fragment.status === "linked" && !fragment.claim) {
      clearAwsMarketplaceClaim();
      setStage({ kind: "alreadyLinked" });
      return;
    }
    const pending = fragment.claim ?? loadAwsMarketplaceClaim();
    if (!pending) {
      setStage({ kind: "expired" });
      return;
    }
    setClaim(pending);
    getAwsMarketplaceClaim(pending)
      .then((info) => {
        if (info?.status !== "pending") {
          clearAwsMarketplaceClaim();
          setStage({ kind: "expired" });
          return;
        }
        setStage({ kind: "signIn", info });
      })
      .catch(() => setStage({ kind: "error", message: notifiers.AWS_MARKETPLACE_UNAVAILABLE }));
  }, []);

  // Once the claim is known to be usable, the signed-in state decides the next step.
  useEffect(() => {
    if (stage.kind !== "signIn" && stage.kind !== "confirm") return;
    if (!signedIn) {
      if (stage.kind === "confirm") setStage({ kind: "signIn", info: stage.info });
      return;
    }
    if (!isAdmin() && !hasAccessToAccount()) {
      setStage({ kind: "notAdmin" });
      return;
    }
    if (stage.kind === "signIn") setStage({ kind: "confirm", info: stage.info });
  }, [stage, signedIn]);

  // The features in the session still belong to the plan before the link. Once the paid plan is on the account,
  // reload them, or Templates, Reports and the rest stay locked until the next sign-in (UAT 2026-10-28). When the
  // link could not apply the plan yet (AWS still provisioning the contract), keep re-reading it for a few minutes.
  const planPending = stage.kind === "linked" && stage.planPending;
  useEffect(() => {
    if (!planPending) return;
    let attempts = 0;
    const timer = window.setInterval(async () => {
      attempts += 1;
      try {
        const plan = await fetchCurrentPlan(account);
        if (Number(plan?.price) > 0) {
          window.clearInterval(timer);
          dispatch(refreshFeatures());
          setStage({ kind: "linked", planPending: false });
          return;
        }
      } catch {
        // a missed read is retried on the next tick
      }
      if (attempts >= strings.AWS_MARKETPLACE_PLAN_POLL_ATTEMPTS) {
        window.clearInterval(timer);
      }
    }, strings.AWS_MARKETPLACE_PLAN_POLL_MS);
    return () => window.clearInterval(timer);
  }, [planPending, account, dispatch]);

  const goToLogin = () => {
    if (isLoggedIn) {
      // A stale session would make Login honour redirectionUrl straight back here (see McpAuthorize).
      dispatch(logOutAction({}));
    }
    const encodedUrl = encodeURIComponent(viewpaths.awsMarketplaceSetupViewPath);
    history.push(`${viewpaths.loginViewPath}?redirectionUrl=${encodedUrl}`);
  };

  const goToRegister = () => history.push(viewpaths.registerViewPath);

  const link = async (info: AwsMarketplaceClaimInfo) => {
    if (!claim) return;
    setStage({ kind: "linking", info });
    try {
      const result = await linkAwsMarketplaceClaim(claim);
      clearAwsMarketplaceClaim();
      dispatch(refreshFeatures());
      const planOutcome = result?.plan;
      setStage({
        kind: "linked",
        planPending: planOutcome === "failed" || planOutcome === "pending",
        planInactive: planOutcome === "expired",
      });
    } catch (error: any) {
      const status = error?.statusCode;
      if (status === 410) {
        clearAwsMarketplaceClaim();
        setStage({ kind: "expired" });
      } else if (status === 403) {
        setStage({ kind: "notAdmin" });
      } else if (status === 401) {
        goToLogin();
      } else {
        const message =
          status === 409 ? getErrorMessageText(error) : notifiers.AWS_MARKETPLACE_LINK_FAILED;
        setStage({ kind: "linkFailed", info, message: message || notifiers.AWS_MARKETPLACE_LINK_FAILED });
      }
    }
  };

  const supportLine = (
    <Typography sx={styles.meta}>
      Need help? Email{" "}
      <Link href={`mailto:${strings.inkfreeSupportEmail}`} sx={styles.link}>
        {strings.inkfreeSupportEmail}
      </Link>
    </Typography>
  );

  const reopenFromAws = (
    <Button
      variant="outlined"
      size="large"
      fullWidth
      href={urls.AWS_MARKETPLACE_SUBSCRIPTIONS}
      target="_blank"
      rel="noopener noreferrer"
    >
      Open AWS Marketplace subscriptions
    </Button>
  );

  const goToDashboard = (
    <Button variant="contained" size="large" fullWidth onClick={() => history.push(viewpaths.dashboardViewPath)}>
      Go to Inkfree
    </Button>
  );

  const renderStage = () => {
    switch (stage.kind) {
      case "checking":
      case "linking":
        return (
          <>
            <Typography component="h1" role="status" sx={styles.heading}>
              {stage.kind === "checking" ? "Checking your AWS Marketplace purchase" : "Linking your subscription"}
            </Typography>
            <Typography sx={styles.body}>One moment.</Typography>
            <LinearProgress sx={styles.progress} />
          </>
        );
      case "error":
        return (
          <>
            <Typography component="h1" sx={styles.heading}>
              We couldn't set up your subscription
            </Typography>
            <Typography role="alert" sx={styles.body}>
              {stage.message} Open your AWS Marketplace subscriptions and choose "Set up your account" for
              Inkfree to try again.
            </Typography>
            <Box sx={styles.actions}>{reopenFromAws}</Box>
            {supportLine}
          </>
        );
      case "expired":
        return (
          <>
            <Typography component="h1" sx={styles.heading}>
              This setup link has expired
            </Typography>
            <Typography sx={styles.body}>
              {notifiers.AWS_MARKETPLACE_EXPIRED_CLAIM} Open your AWS Marketplace subscriptions and choose
              "Set up your account" for Inkfree to get a new one.
            </Typography>
            <Box sx={styles.actions}>{reopenFromAws}</Box>
            {supportLine}
          </>
        );
      case "alreadyLinked":
        return (
          <>
            <Typography component="h1" sx={styles.heading}>
              Your subscription is already set up
            </Typography>
            <Typography sx={styles.body}>
              This AWS Marketplace subscription is linked to an Inkfree account. Sign in to start using it.
            </Typography>
            <Box sx={styles.actions}>
              {signedIn ? (
                goToDashboard
              ) : (
                <Button variant="contained" size="large" fullWidth onClick={goToLogin}>
                  Sign in
                </Button>
              )}
            </Box>
            {supportLine}
          </>
        );
      case "signIn":
        return (
          <>
            <Typography component="h1" sx={styles.heading}>
              Finish setting up Inkfree
            </Typography>
            <Typography sx={styles.body}>
              Your AWS Marketplace purchase
              {stage.info.awsAccount ? ` (AWS account ${stage.info.awsAccount})` : ""} is ready. Sign in to the
              Inkfree account it should be billed to, or create one.
            </Typography>
            <Box sx={styles.actions}>
              <Button variant="contained" size="large" fullWidth onClick={goToLogin}>
                Sign in
              </Button>
              <Button variant="outlined" size="large" fullWidth onClick={goToRegister}>
                Create an account
              </Button>
            </Box>
            <Typography sx={styles.meta}>
              New to Inkfree? After you verify your email, come back to this page in the same browser, or
              reopen "Set up your account" from AWS Marketplace, to finish.
            </Typography>
            {supportLine}
          </>
        );
      case "confirm":
      case "linkFailed":
        return (
          <>
            <Typography component="h1" sx={styles.heading}>
              Link your AWS Marketplace subscription
            </Typography>
            <Typography sx={styles.body}>
              Billing for{accountName ? ` ${accountName}` : " this account"} will move to AWS Marketplace,
              and its plan and seats will follow what you bought there.
            </Typography>
            {stage.kind === "linkFailed" && (
              <Typography role="alert" sx={{ ...styles.body, color: "error.main" }}>
                {stage.message}
              </Typography>
            )}
            <Box sx={styles.actions}>
              <Button variant="contained" size="large" fullWidth onClick={() => link(stage.info)}>
                Link to{accountName ? ` ${accountName}` : " this account"}
              </Button>
              <Button variant="outlined" size="large" fullWidth onClick={goToLogin}>
                Use a different account
              </Button>
            </Box>
            {supportLine}
          </>
        );
      case "notAdmin":
        return (
          <>
            <Typography component="h1" sx={styles.heading}>
              An administrator needs to finish setup
            </Typography>
            <Typography sx={styles.body}>{notifiers.AWS_MARKETPLACE_NOT_ADMIN}</Typography>
            <Box sx={styles.actions}>
              <Button variant="outlined" size="large" fullWidth onClick={goToLogin}>
                Sign in as an administrator
              </Button>
            </Box>
            {supportLine}
          </>
        );
      case "linked":
        if (stage.planInactive) {
          return (
            <>
              <Typography component="h1" role="status" sx={styles.heading}>
                Your AWS Marketplace subscription isn't active
              </Typography>
              <Typography sx={styles.body}>
                {notifiers.AWS_MARKETPLACE_INACTIVE} Check the subscription in AWS Marketplace, or contact support.
              </Typography>
              <Box sx={styles.actions}>
                {reopenFromAws}
                {goToDashboard}
              </Box>
              {supportLine}
            </>
          );
        }
        return (
          <>
            <Typography component="h1" role="status" sx={styles.heading}>
              You're all set
            </Typography>
            <Typography sx={styles.body}>
              Your AWS Marketplace subscription is linked and is now billed through AWS.
              {stage.planPending
                ? " Your plan and seats are still being applied and will appear shortly."
                : " Your plan and seats are applied."}
            </Typography>
            <Box sx={styles.actions}>{goToDashboard}</Box>
          </>
        );
    }
  };

  return (
    <AuthLayout>
      <Box>
        <Box aria-hidden="true" sx={styles.accentBar} />
        <Card elevation={0} sx={styles.card}>
          <CardContent sx={styles.cardContent}>
            <Box sx={styles.column}>
              <Box component="img" src={Logo} alt="Inkfree e-Sign" sx={styles.logo} />
              {renderStage()}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </AuthLayout>
  );
};

export default AwsMarketplaceSetup;
